import React, { useEffect, useReducer, useRef, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import api from '@utils/axios';
import { Upload, ExternalDropZone } from '@progress/kendo-react-upload';
import I18n, { translate } from '@utils/i18n';
import { photosPath } from 'routes';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import { Loader, Button } from '@ui';
import { actionTypes, reducer } from './reducer';
import NavigationPanel from './nested/NavigationPanel';
import MonthGroup from './nested/MonthGroup';
import { PhotoGalleryProvider } from './PhotoGalleryContext';
import './PhotoGallery.scss';

const namespace = 'features.photo_gallery.index';
const ACCESS_DENIED_STATUSES = [403, 404];

function PhotoGallery({ buildingId, inspectionId, workOrderId, deficiencyId, isCustomerPortal, hideTimeline, selection }) {
  const [initialLoad, setInitialLoad] = useState(true);
  const [uploading, setUploading] = useState(false);
  const photosUploading = useRef(0);
  const [visibleElements, setVisibleElements] = useState([]);
  const uploadRef = useRef();
  const [state, dispatch] = useReducer(reducer, {
    photos: {},
    expanded: {},
    photosCount: {},
    focused: null
  });
  const [failedUploads, setFailedUploads] = useState([]);
  const uploadingFilesRef = useRef({});
  const uploadStartTimeRef = useRef({});
  const uploadTimeoutRef = useRef({});
  const uploadCorrelationIdRef = useRef({});
  const addedPhotoIdsRef = useRef(new Set());
  const erroredUploadTimesRef = useRef(new Set());
  const retryingUploadsRef = useRef(new Set());

  const pageDetails = useMemo(() => {
    if (inspectionId)
      return {
        type: 'inspection',
        id: inspectionId
      };
    if (workOrderId)
      return {
        type: 'work_order',
        id: workOrderId
      };
    if (deficiencyId)
      return {
        type: 'deficiency',
        id: deficiencyId
      };
    return {
      type: 'building',
      id: buildingId
    };
  }, []);

  const currentYearMonth = useMemo(() => DateTime.now().toFormat('yyyyLL'), []);

  const authToken = useMemo(() => document.querySelector('meta[name="csrf-token"]').getAttribute('content'), []);

  const monthGroupsRef = useRef({});

  const getErrorMessage = (error) => {
    if (error?.code === 'NETWORK_ISSUE') {
      return translate('network_issue', { namespace });
    }

    if (error?.response) {
      const data = error.response.data;
      if (data?.error) return data.error;
      if (data?.message) return data.message;
    }

    return translate('upload_error', { namespace });
  };

  useEffect(() => {
    if (buildingId || isCustomerPortal) return;

    const uploadDropZone = document.querySelector('#uploadDropZone .k-dropzone-inner');
    const listener = () => {
      document.getElementById('upload-select-button').click();
    };
    uploadDropZone.addEventListener('click', listener);
    return () => {
      uploadDropZone.removeEventListener('click', listener);
    };
  }, [isCustomerPortal]);

  useEffect(() => {
    api
      .get(
        photosPath({
          building_id: buildingId,
          inspection_id: inspectionId,
          work_order_id: workOrderId,
          deficiency_id: deficiencyId,
          format: 'json'
        })
      )
      .then(({ data }) => {
        dispatch({ type: actionTypes.DATA_LOADED, data });
        setInitialLoad(false);
      })
      .catch(() => {
        dispatch({ type: actionTypes.DATA_LOADED, data: [] });
        setInitialLoad(false);
        toast.error(translate('load_photo_error', { namespace }), {
          position: 'bottom-right',
          autoClose: 3000,
          closeOnClick: true
        });
      });
  }, []);

  useEffect(() => {
    if (initialLoad) return;

    const badge = document.getElementById('photos-count-badge');
    if (!badge) return;

    const total = Object.values(state.photosCount || {}).reduce((sum, value) => sum + Number(value || 0), 0);
    badge.textContent = String(total);
    badge.removeAttribute('data-empty');
    badge.classList.remove('count-badge--empty');
  }, [initialLoad, state.photosCount]);

  const onCollapseMonthGroup = (yearMonth) => {
    dispatch({ type: actionTypes.MONTH_COLLAPSED, yearMonth });
  };

  const onExpandMonthGroup = (yearMonth) => {
    dispatch({ type: actionTypes.MONTH_FOCUSED, focused: yearMonth });

    if (!state.expanded[yearMonth] && state.photos[yearMonth] === undefined) {
      api
        .get(
          photosPath({
            building_id: buildingId,
            inspection_id: inspectionId,
            work_order_id: workOrderId,
            deficiency_id: deficiencyId,
            year_month: yearMonth,
            format: 'json'
          })
        )
        .then(({ data }) => {
          dispatch({ type: actionTypes.DATA_LOADED, data });
        })
        .catch(() => {
          dispatch({ type: actionTypes.DATA_LOADED, data: { photos: { [yearMonth]: [], photosCount: null } } });
          toast.error(translate('load_photo_error', { namespace }), {
            position: 'bottom-right',
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }

    dispatch({ type: actionTypes.MONTH_EXPANDED, yearMonth });
  };

  useEffect(() => {
    if (!state.focused || !monthGroupsRef.current[state.focused]) return;
    monthGroupsRef.current[state.focused].scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [state.focused]);

  const monthsArray = useMemo(() => Object.entries(state.photosCount).reverse(), [state.photosCount]);

  // Helper function to find the correct fileId for an upload event
  const findFileId = (fileName, fileUid) => {
    let fileId = null;

    // Strategy 1: Match by uid (most reliable when available)
    if (fileUid) {
      fileId = Object.keys(uploadingFilesRef.current).find((id) => {
        return id === fileUid || id.includes(fileUid);
      });
    }

    // Strategy 2: Match by filename in tracked file data or in the key itself
    // Keys can be like "photo.jpg_1234567890" when uid is unavailable
    if (!fileId && fileName) {
      fileId = Object.keys(uploadingFilesRef.current).find((id) => {
        const trackedFile = uploadingFilesRef.current[id];
        // Check if tracked file's name matches
        if (trackedFile?.name === fileName || trackedFile?.file?.name === fileName) {
          return true;
        }
        // Check if the key itself starts with the filename (for timestamp-based keys)
        if (id.startsWith(fileName)) {
          return true;
        }
        return false;
      });
    }

    // Strategy 3: If single file remaining, must be this one
    if (!fileId) {
      const allFileIds = Object.keys(uploadingFilesRef.current);
      if (allFileIds.length === 1) {
        fileId = allFileIds[0];
      }
    }

    // Strategy 4: Match by newest upload start time (LIFO - last in, first out)
    // When uploads complete out of order (small files finish before large files),
    // matching the most recent upload event is safer than oldest
    if (!fileId) {
      const allFileIds = Object.keys(uploadingFilesRef.current);
      if (allFileIds.length > 0) {
        let newestFileId = allFileIds[0];
        let newestTime = uploadStartTimeRef.current[newestFileId] || -Infinity;

        allFileIds.forEach((id) => {
          const startTime = uploadStartTimeRef.current[id] || -Infinity;
          if (startTime > newestTime) {
            newestTime = startTime;
            newestFileId = id;
          }
        });

        fileId = newestFileId;
      }
    }

    return fileId;
  };

  // Helper function to clean up tracking refs and timeout for a file
  const cleanupFileTracking = (fileId) => {
    if (!fileId) return;

    delete uploadingFilesRef.current[fileId];
    delete uploadStartTimeRef.current[fileId];
    delete uploadCorrelationIdRef.current[fileId];
    clearTimeout(uploadTimeoutRef.current[fileId]);
    delete uploadTimeoutRef.current[fileId];
  };

  const onUploadFinished = (event) => {
    const fileName = event.files?.[0]?.name;
    const fileUid = event.files?.[0]?.uid;
    const fileId = findFileId(fileName, fileUid);

    // Checked before success: on a failed request Kendo puts the axios response
    // object in event.response.response, which the success check would accept as a photo.
    if (event.response?.status >= 400) {
      const accessDenied = ACCESS_DENIED_STATUSES.includes(event.response.status);

      if (fileId && !accessDenied) {
        const fileData = uploadingFilesRef.current[fileId];
        if (fileData) {
          const errorMsg = getErrorMessage({ response: { status: event.response?.status } });
          setFailedUploads((prev) => [...prev, { id: fileId, file: fileData.file, error: errorMsg }]);
        }
      }

      cleanupFileTracking(fileId);

      photosUploading.current -= 1;
      if (photosUploading.current === 0) setUploading(false);

      toast.error(translate(accessDenied ? 'upload_not_allowed' : 'upload_failed_retry', { namespace }), {
        position: 'bottom-right',
        duration: 5000
      });
      return;
    }

    // Success case: check both response.response and response directly (for different server response formats)
    if (event.response?.response || (event.response && typeof event.response === 'object' && event.response.id)) {
      const photo = event.response?.response || event.response;
      dispatch({ type: actionTypes.PHOTO_ADDED, yearMonth: currentYearMonth, newPhoto: photo });
      addedPhotoIdsRef.current.add(photo.id);

      cleanupFileTracking(fileId);

      photosUploading.current -= 1;
      if (photosUploading.current === 0) setUploading(false);
      return;
    }

    if (!event.response) {
      if (fileId) {
        const fileData = uploadingFilesRef.current[fileId];
        if (fileData) {
          const errorMsg = getErrorMessage({ code: 'NETWORK_ISSUE' });
          setFailedUploads((prev) => [...prev, { id: fileId, file: fileData.file, error: errorMsg }]);
        }
      }

      cleanupFileTracking(fileId);

      photosUploading.current -= 1;
      if (photosUploading.current === 0) setUploading(false);

      toast.error(translate('upload_failed_retry', { namespace }), {
        position: 'bottom-right',
        duration: 5000
      });
      return;
    }

    // Fallback case: upload completed but no clear response - still need to decrement counter
    cleanupFileTracking(fileId);

    photosUploading.current -= 1;
    if (photosUploading.current === 0) setUploading(false);
  };

  const onUploadError = (event) => {
    const file = event.files?.[0];
    const fileName = file?.name;
    const fileUid = file?.uid;
    const fileId = findFileId(fileName, fileUid);

    // Record upload time in errored set if present
    const uploadTime = uploadStartTimeRef.current[fileId];
    if (uploadTime) {
      erroredUploadTimesRef.current.add(uploadTime);
    }

    // Add to failed uploads for retry UI
    if (fileId) {
      const fileData = uploadingFilesRef.current[fileId];
      if (fileData) {
        const errorMsg = getErrorMessage(event.error || { code: 'UPLOAD_ERROR' });
        setFailedUploads((prev) => [...prev, { id: fileId, file: fileData.file, error: errorMsg }]);
      }
    }

    cleanupFileTracking(fileId);

    // Decrement upload counter (guard against negative values)
    photosUploading.current = Math.max(0, photosUploading.current - 1);
    if (photosUploading.current === 0) setUploading(false);

    toast.error(translate('upload_failed_retry', { namespace }), {
      position: 'bottom-right',
      duration: 5000
    });
  };

  const onBeforeUpload = (event) => {
    setUploading(true);
    photosUploading.current += 1;

    const file = event.files[0];
    const rawFile = file.getRawFile ? file.getRawFile() : file;
    const fileId = file.uid || `${rawFile.name}_${Date.now()}`;
    const uploadTime = Date.now();
    const uploadCorrelationId = uuidv4();
    uploadingFilesRef.current[fileId] = { file: rawFile, name: rawFile.name };
    uploadStartTimeRef.current[fileId] = uploadTime;
    uploadCorrelationIdRef.current[fileId] = uploadCorrelationId;

    const timeoutId = setTimeout(() => {
      if (!uploadingFilesRef.current[fileId]) {
        delete uploadTimeoutRef.current[fileId];
        return;
      }

      const fileData = uploadingFilesRef.current[fileId];
      if (fileData) {
        const errorMsg = getErrorMessage({ code: 'NETWORK_ISSUE' });
        setFailedUploads((prev) => [...prev, { id: fileId, file: fileData.file, error: errorMsg }]);
        delete uploadingFilesRef.current[fileId];
        delete uploadStartTimeRef.current[fileId];
        delete uploadCorrelationIdRef.current[fileId];
        photosUploading.current -= 1;
        if (photosUploading.current === 0) setUploading(false);
      }
      toast.error(translate('upload_photo_error', { namespace }), {
        position: 'bottom-right',
        duration: 5000
      });
      delete uploadTimeoutRef.current[fileId];
    }, 65000);

    uploadTimeoutRef.current[fileId] = timeoutId;
  };

  const onChangeVisibility = ({ yearMonth, isVisible }) => {
    const sortFunction = (a, b) => {
      const indexA = monthsArray.findIndex(([id]) => id === a);
      const indexB = monthsArray.findIndex(([id]) => id === b);
      if (indexA === indexB) return 0;
      if (indexA < indexB) return -1;
      return 1;
    };
    if (isVisible) {
      setVisibleElements((oldElements) => {
        if (oldElements.includes(yearMonth)) return oldElements;
        return [...oldElements, yearMonth].sort(sortFunction);
      });
    } else {
      setVisibleElements((oldElements) => {
        return oldElements.filter((element) => element !== yearMonth).sort(sortFunction);
      });
    }
  };

  const onValidationError = (e) => {
    if (e.affectedFiles.some((file) => file.validationErrors?.length)) {
      toast.error(translate('upload_photo_error', { namespace }), {
        position: 'bottom-right',
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const retryUpload = (failedUpload) => {
    // Prevent concurrent retries of the same file
    if (retryingUploadsRef.current.has(failedUpload.id)) {
      return;
    }
    retryingUploadsRef.current.add(failedUpload.id);

    setFailedUploads((prev) => prev.filter((upload) => upload.id !== failedUpload.id));

    const rawFile = failedUpload.file;
    const formData = new FormData();
    formData.append('image', rawFile);
    formData.append('authenticity_token', authToken);

    setUploading(true);
    photosUploading.current += 1;

    const fileId = `${rawFile.name}_${Date.now()}_${Math.random()}`;
    const uploadTime = Date.now();
    uploadingFilesRef.current[fileId] = { file: rawFile, name: rawFile.name };
    uploadStartTimeRef.current[fileId] = uploadTime;

    const startTime = Date.now();
    const minLoaderTime = 800;

    const timeoutId = setTimeout(() => {
      if (!uploadingFilesRef.current[fileId]) {
        delete uploadTimeoutRef.current[fileId];
        return;
      }

      const fileData = uploadingFilesRef.current[fileId];
      if (fileData) {
        const errorMsg = getErrorMessage({ code: 'NETWORK_ISSUE' });
        setFailedUploads((prev) => [...prev, { id: fileId, file: fileData.file, error: errorMsg }]);
        delete uploadingFilesRef.current[fileId];
        delete uploadStartTimeRef.current[fileId];
        photosUploading.current -= 1;
        if (photosUploading.current === 0) setUploading(false);
        toast.error(translate('upload_timeout', { namespace }), {
          position: 'bottom-right',
          duration: 5000
        });
      }
      delete uploadTimeoutRef.current[fileId];
      retryingUploadsRef.current.delete(failedUpload.id);
    }, 30000);

    uploadTimeoutRef.current[fileId] = timeoutId;

    api
      .post(
        photosPath({
          inspection_id: inspectionId,
          work_order_id: workOrderId,
          deficiency_id: deficiencyId,
          format: 'json',
          _options: true
        }),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      .then((response) => {
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoaderTime - elapsed);

        clearTimeout(uploadTimeoutRef.current[fileId]);
        delete uploadTimeoutRef.current[fileId];

        setTimeout(() => {
          // Only add photo if it hasn't been added already (deduplication)
          if (response.data && !addedPhotoIdsRef.current.has(response.data.id)) {
            dispatch({ type: actionTypes.PHOTO_ADDED, yearMonth: currentYearMonth, newPhoto: response.data });
            addedPhotoIdsRef.current.add(response.data.id);
            toast.success(translate('upload_success', { namespace }), {
              position: 'bottom-right',
              duration: 3000
            });
          }
          delete uploadingFilesRef.current[fileId];
          delete uploadStartTimeRef.current[fileId];
          photosUploading.current -= 1;
          if (photosUploading.current === 0) setUploading(false);
          retryingUploadsRef.current.delete(failedUpload.id);
        }, remainingTime);
      })
      .catch((error) => {
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoaderTime - elapsed);

        clearTimeout(uploadTimeoutRef.current[fileId]);
        delete uploadTimeoutRef.current[fileId];

        setTimeout(() => {
          const accessDenied = ACCESS_DENIED_STATUSES.includes(error.response?.status);
          const errorMsg = getErrorMessage(error);
          if (!accessDenied) {
            setFailedUploads((prev) => [...prev, { id: fileId, file: rawFile, error: errorMsg }]);
          }
          delete uploadingFilesRef.current[fileId];
          delete uploadStartTimeRef.current[fileId];
          photosUploading.current -= 1;
          if (photosUploading.current === 0) setUploading(false);
          const message = accessDenied
            ? translate('upload_not_allowed', { namespace })
            : translate('retry_failed', { namespace, error: errorMsg });
          toast.error(message, {
            position: 'bottom-right',
            duration: 5000
          });
          retryingUploadsRef.current.delete(failedUpload.id);
        }, remainingTime);
      });
  };

  const removeFailedUpload = (failedUploadId) => {
    setFailedUploads((prev) => prev.filter((upload) => upload.id !== failedUploadId));
  };

  return (
    <PhotoGalleryProvider isCustomerPortal={isCustomerPortal}>
      <div className="photo-gallery-container">
        <div className="" style={{ alignItems: 'stretch', display: 'flex', width: '100%' }}>
          <div className="tab__major">
            {!buildingId && !isCustomerPortal && (
              <>
                <ExternalDropZone
                  customHint={<UploadCustomHint />}
                  customNote={<UploadCustomNote />}
                  id="uploadDropZone"
                  uploadRef={uploadRef}
                  style={{ marginBottom: '2.4rem', marginRight: '2.4rem', borderStyle: 'dashed' }}
                />
                {/* Without defaultFiles Kendo treats the Upload as controlled, finds no file when a request
                    fails and never calls onStatusChange, so the loader would never stop. */}
                <Upload
                  id="upload-select-button"
                  ref={uploadRef}
                  defaultFiles={[]}
                  batch={false}
                  multiple
                  disabled={uploading}
                  restrictions={{ allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif'], maxFileSize: 5 * 1024 * 1024 }}
                  showFileList={false}
                  withCredentials
                  onStatusChange={onUploadFinished}
                  onAdd={onValidationError}
                  onError={onUploadError}
                  saveField="image"
                  saveUrl={photosPath({
                    inspection_id: inspectionId,
                    work_order_id: workOrderId,
                    deficiency_id: deficiencyId,
                    authenticity_token: authToken,
                    format: 'json',
                    _options: true
                  })}
                  onBeforeUpload={onBeforeUpload}
                />
                {uploading && (
                  <div className="loaderContainer">
                    <Loader />
                  </div>
                )}
              </>
            )}
            {failedUploads.length > 0 && (
              <section className="workspace__section --expanded --active" style={{ marginBottom: '1.5rem' }}>
                <div>
                  <Button className="section__title">
                    <h2 className="section__label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className="fa-solid fa-triangle-exclamation" style={{ color: '#dc3545' }} />
                      {translate('failed_uploads', { namespace })}
                    </h2>
                    <span className="section__count">
                      {failedUploads.length} {translate('photos', { namespace })}
                    </span>
                  </Button>
                </div>
                <div className="section__content--table">
                  <article className="qmb-table--02--x-full">
                    <table>
                      <thead>
                        <tr>
                          <th className="--primary">{translate('photo', { namespace })}</th>
                          <th>{translate('error_column', { namespace })}</th>
                          <th style={{ width: '120px', textAlign: 'center' }}>
                            {translate('actions_column', { namespace })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {failedUploads.map((failedUpload) => {
                          const isNetworkIssue = failedUpload.error === translate('network_issue', { namespace });
                          return (
                            <tr key={failedUpload.id}>
                              <td className="--primary">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <i className="fa-solid fa-file-image" style={{ color: '#6c757d' }} />
                                  {failedUpload.file.name}
                                </div>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {isNetworkIssue && (
                                    <i
                                      className="fa-solid fa-wifi"
                                      style={{ color: '#dc3545' }}
                                      title="Network issue"
                                    />
                                  )}
                                  <span style={{ color: '#dc3545', fontSize: '13px' }}>{failedUpload.error}</span>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {!isNetworkIssue && (
                                  <Button
                                    onClick={() => retryUpload(failedUpload)}
                                    className="qmb-button--small"
                                    style={{ marginRight: '0.5rem' }}
                                    title={translate('retry_button', { namespace })}
                                    aria-label={translate('retry_button', { namespace })}>
                                    <i className="fa-solid fa-rotate-right" />
                                  </Button>
                                )}
                                <Button
                                  onClick={() => removeFailedUpload(failedUpload.id)}
                                  className="qmb-button--small"
                                  title={translate('remove_button', { namespace })}
                                  aria-label={translate('remove_button', { namespace })}>
                                  <i className="fa-solid fa-times" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </article>
                </div>
              </section>
            )}
            {initialLoad && <Loader vertical title={I18n.t('generic.loading')} />}
            {!initialLoad && !Object.entries(state.photosCount).length && (
              <div style={{ textAlign: 'center', marginTop: '25px' }}>{translate('no_photos', { namespace })}</div>
            )}
            {!initialLoad &&
              Object.entries(state.photosCount).length > 0 &&
              monthsArray.map(([yearMonth, count]) => (
                <MonthGroup
                  key={yearMonth}
                  innerRef={(group) => {
                    monthGroupsRef.current[yearMonth] = group;
                  }}
                  onExpand={onExpandMonthGroup}
                  onCollapse={onCollapseMonthGroup}
                  yearMonth={yearMonth}
                  expanded={state.expanded[yearMonth]}
                  count={count}
                  photos={state.photos[yearMonth]}
                  dispatch={dispatch}
                  pageDetails={pageDetails}
                  onChangeVisibility={onChangeVisibility}
                  selection={selection}
                />
              ))}
            <div style={{ height: 'calc(100vh - 280px)' }} />
          </div>
        </div>
        {Object.entries(state.photosCount).length > 0 && !hideTimeline && (
          <NavigationPanel state={state} onExpand={onExpandMonthGroup} focusElement={visibleElements[0]} />
        )}
      </div>
    </PhotoGalleryProvider>
  );
}

function UploadCustomHint() {
  return (
    <div>
      <div style={{ textAlign: 'center' }}>
        <i style={{ fontSize: '32px', color: '#1967D2' }} className="fa-light fa-cloud-arrow-up" />
      </div>
      <div style={{ textAlign: 'center' }}>
        <span style={{ color: '#1967D2' }}>{translate('upload_hint_1', { namespace })}</span>
        &nbsp;
        {translate('upload_hint_2', { namespace })}
        <br />
        {translate('upload_hint_3', { namespace })}
      </div>
    </div>
  );
}

function UploadCustomNote() {
  return (
    <div style={{ textAlign: 'center' }}>
      {translate('upload_note_1', { namespace })}
      <br />
      {translate('upload_note_2', { namespace })}
    </div>
  );
}

PhotoGallery.propTypes = {
  buildingId: PropTypes.number,
  inspectionId: PropTypes.number,
  workOrderId: PropTypes.number,
  deficiencyId: PropTypes.number,
  isCustomerPortal: PropTypes.bool,
  hideTimeline: PropTypes.bool,
  selection: PropTypes.shape({
    includedIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    onToggle: PropTypes.func.isRequired
  })
};

PhotoGallery.defaultProps = {
  buildingId: undefined,
  inspectionId: undefined,
  workOrderId: undefined,
  deficiencyId: undefined,
  isCustomerPortal: false,
  hideTimeline: false,
  selection: undefined
};

export default PhotoGallery;
